import React from 'react';
import { useDeviceType } from '@cian/ui-kit';
import cn from 'clsx';
import { ETestIds } from 'shared/constants/testIds';
import { ShadowWrapper } from 'shared/components/ShadowWrapper';

import {
  Title,
  ITitleProps,
  OfferBtn,
  IOfferBtnProps,
  Description,
  IDescriptionProps,
  LabelList,
  ILabelListProps,
  ConditionList,
  IConditionListProps,
  Banner,
  IBannerProps,
  Advertiser,
  IAdvertiserProps,
  Requirements,
  IRequirementsProps,
} from './components';
import styles from './styles.css';
import { useSelector } from 'react-redux';
import { selectActionUrl } from '../../selectors/landing';
import { selectBankTitlesToIdsMap } from '../../selectors/dictionaries';
import { ETargetElementForAnalytics } from '../../utils/tracking/types';
import { NoSsr } from '@cian/react-utils';
import { useCreditInfoModal } from 'shared/hooks/useCreditInfoModal';
import { useTracking } from '../../utils/tracking/useTracking';

export interface IOfferCardProps {
  title: ITitleProps;
  button?: IOfferBtnProps;
  description: IDescriptionProps;
  conditions: IConditionListProps;
  requirements?: IRequirementsProps['requirements'];
  promotion?: IAdvertiserProps & IBannerProps;
  labels?: ILabelListProps['labels'];
  multiOfferBtn?: React.ReactNode;
  placeOnList: number;
}

export const OfferCard: React.FC<IOfferCardProps> = ({
  title,
  button,
  conditions,
  requirements,
  promotion,
  description,
  labels,
  multiOfferBtn,
  placeOnList,
}) => {
  const deviceType = useDeviceType();

  const bankTitlesToIdsMap = useSelector(selectBankTitlesToIdsMap);
  const hasLabels = labels && labels.length >= 1;
  const hasPromotion = promotion && promotion.bannerImgPaths && promotion.aboutAdvertiser;
  const hasRequirements = requirements && requirements.length;
  const isMultiOffer = Boolean(multiOfferBtn);
  const { handleModalOpen } = useCreditInfoModal();
  const actionUrl = useSelector(selectActionUrl) as string;
  const { trackClickOnCard } = useTracking();

  const handleClickCard = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();

    trackClickOnCard({
      place: placeOnList,
      bank: title.name,
      isMultiOffer,
      type: button ? button.analyticEventKey : 'oshk',
    });

    if (title.modalContent) {
      const fillApplicationParams = {
        authorizationRequired: true,
        bankName: title.name,
        path: button ? button.path : actionUrl,
        place: placeOnList,
        analyticEventKey: button ? button.analyticEventKey : 'oshk',
        targetElementForAnalytics: ETargetElementForAnalytics.CARD,
        isMultiOffer,
        text: button ? button.text : undefined,
      };

      handleModalOpen(title.modalContent, fillApplicationParams);
    }
  };

  return (
    <ShadowWrapper onClick={handleClickCard}>
      <div className={styles['root']} data-testid={`${ETestIds.OfferCard} bankId-${bankTitlesToIdsMap[title.name]}`}>
        <Title {...title} />
        {hasPromotion ? <Banner bannerImgPaths={promotion.bannerImgPaths} /> : null}
        <div className={cn(styles['container'], styles[deviceType])}>
          {Object.values(description).some(v => v !== undefined) ? <Description {...description} /> : null}
          {hasLabels ? <LabelList labels={labels} /> : null}
          {conditions ? <ConditionList {...conditions} /> : null}
          {hasRequirements ? <Requirements requirements={requirements} /> : null}
          {hasPromotion ? <Advertiser aboutAdvertiser={promotion.aboutAdvertiser} /> : null}
          <NoSsr>{button && <OfferBtn {...button} isMultiOffer={isMultiOffer} place={placeOnList} />}</NoSsr>
          {isMultiOffer ? multiOfferBtn : null}
        </div>
      </div>
    </ShadowWrapper>
  );
};
